/**
 * Como a tela fala com o servidor.
 *
 * Três coisas valem para todos os pedidos:
 *
 * **O endereço é relativo.** Nunca `https://api...`: o painel é servido pelo mesmo servidor
 * que responde `/v1`, então pedir `/v1/painel/inicio` fala com quem entregou a página. É o
 * que faz a sessão viajar num cookie fechado e dispensa CORS.
 *
 * **A sessão não passa por aqui.** Ela mora num cookie que este código não consegue ler
 * (`HttpOnly`), e o navegador anexa sozinho. Se um script estranho um dia entrar na página,
 * não tem sessão para roubar.
 *
 * **Erro do servidor vira frase em português.** O servidor já responde
 * `{ erro: { codigo, mensagem } }` com uma frase escrita para ser lida; a tela mostra essa
 * frase, e não inventa outra.
 */

export class ErroDoServidor extends Error {
  readonly codigo: string
  readonly situacao: number

  constructor(situacao: number, codigo: string, mensagem: string) {
    super(mensagem)
    this.name = 'ErroDoServidor'
    this.codigo = codigo
    this.situacao = situacao
  }
}

/** A sessão terminou (ou nunca existiu): a tela volta para a entrada. */
export function ehSessaoTerminada(erro: unknown): boolean {
  return erro instanceof ErroDoServidor && erro.situacao === 401
}

async function pedir<T>(metodo: 'GET' | 'POST', caminho: string, corpo?: unknown): Promise<T> {
  let resposta: Response
  try {
    resposta = await fetch(caminho, {
      method: metodo,
      // O cookie vai junto. Sem isto o navegador não manda cookie em `fetch`.
      credentials: 'same-origin',
      headers: corpo === undefined ? {} : { 'content-type': 'application/json' },
      ...(corpo === undefined ? {} : { body: JSON.stringify(corpo) })
    })
  } catch {
    // Internet caiu, servidor reiniciando: é o erro mais comum e merece frase própria.
    throw new ErroDoServidor(0, 'sem_conexao', 'Não foi possível falar com o servidor. Confira a internet e tente de novo.')
  }

  const texto = await resposta.text()
  const json: unknown = texto.length > 0 ? tentarLer(texto) : null

  if (!resposta.ok) {
    const erro = (json as { erro?: { codigo?: string; mensagem?: string } } | null)?.erro
    throw new ErroDoServidor(
      resposta.status,
      erro?.codigo ?? 'falha',
      erro?.mensagem ?? 'O servidor não conseguiu atender. Tente de novo em instantes.'
    )
  }

  return json as T
}

function tentarLer(texto: string): unknown {
  try {
    return JSON.parse(texto)
  } catch {
    return null
  }
}

export const api = {
  get: <T>(caminho: string) => pedir<T>('GET', caminho),
  post: <T>(caminho: string, corpo?: unknown) => pedir<T>('POST', caminho, corpo)
}

// ------------------------------------------------------------------ o que o servidor devolve

/** A resposta de `/v1/painel/eu`: ou ninguém entrou, ou é alguém e vem tudo. */
export type QuemEsta = { dentro: false } | ({ dentro: true } & Eu)

export interface Eu {
  /** Se a conta tem segundo fator ligado. O Início usa para avisar quem ainda não ligou. */
  tem_segundo_fator: boolean
  /**
   * A conta é nova e precisa ligar o segundo fator antes de ver qualquer coisa.
   *
   * Quem decide isso é o servidor, em toda rota. Aqui ela serve para a tela não oferecer um
   * caminho que vai dar 403 na cara da pessoa: com ela marcada, o painel abre numa tela só.
   */
  deve_ligar_segundo_fator: boolean
  id: string
  nome: string
  email: string
  campo_id: string
  usuario_id: string | null
  nome_no_notebook: string | null
  /**
   * O cargo da pessoa, **já no nome de tela**, ou nulo quando ela não tem.
   *
   * A lista mora no servidor, e não aqui: assim ela cresce sem o painel precisar ser montado
   * de novo. **O cargo não dá poder**, e nenhuma tela decide nada com ele; quem decide o que a
   * pessoa vê é a grade de permissões.
   */
  cargo: string | null
  entra_com_a_senha_do_notebook: boolean
  deve_trocar_senha: boolean
  e_admin_maximo: boolean
  congregacao_propria: string | null
  ve_o_campo_inteiro: boolean
  congregacoes_que_ve: string[] | null
  /** Só na resposta da entrada: ela foi feita com um código de emergência. */
  entrou_com_codigo_de_emergencia?: { sobram: number } | null
  /** As telas de Notebooks, Pessoas e Auditoria aparecem no caminho. */
  administra_o_campo: boolean
  /** A Auditoria aparece, mesmo para quem não administra (o Conselho fiscal, por exemplo). */
  ve_a_auditoria: boolean
  /** Dá para cortar notebook e devolver acesso. */
  pode_administrar_notebooks: boolean
  /** Dá para gerar código de notebook novo. Só o administrador máximo. */
  pode_inscrever_notebook: boolean
  /**
   * ESTA SESSÃO ESTÁ VENDO O CAMPO DE TREINAMENTO.
   *
   * Quem decide é o SERVIDOR, pela sessão, e não o endereço da página. Faixa decidida por
   * `?treinamento=1` é faixa que não aparece quando importa: basta recarregar sem a pergunta,
   * ou guardar o endereço sem ela.
   */
  treinamento: boolean
  /** O nome do Campo que está sendo visto. A faixa diz qual é. */
  campo_que_estou_vendo: string | null
  /** Existe treinamento para onde ir? Falso esconde o botão, e o servidor recusa de qualquer jeito. */
  tem_treinamento: boolean
  /**
   * Quem abre cada tela da fase 6 do espelho. Só para não oferecer um caminho que dá recusa; quem
   * decide é o servidor, em cada rota.
   */
  alcance_das_telas: {
    dizimistas: Alcance
    repasses: Alcance
    agenda: Alcance
    repasses_do_campo: boolean
    conferencia_do_cartao: boolean
  }
}

export interface Alcance {
  tudo: boolean
  congregacoes: string[]
}

/** A pessoa abre aquela tela daquela congregação? Espelho do `pode` do servidor. */
export function alcanca(alcance: Alcance | undefined, congregacaoId: string): boolean {
  return alcance !== undefined && (alcance.tudo || alcance.congregacoes.includes(congregacaoId))
}

export type SituacaoDoMes = 'ABERTO' | 'FECHADO' | 'TRAVADO' | 'NAO_FECHOU'

export interface LinhaDoInicio {
  congregacao: { id: string; nome: string; e_sede: boolean }
  competencia: string
  entradas: number
  saidas: number
  situacao: SituacaoDoMes
  versao_do_fechamento: number | null
  motivo_da_reabertura: string | null
}

export interface NotebookSemEnviar {
  id: string
  nome: string
  congregacao: string | null
  ultimo_envio: string | null
  dias_sem_enviar: number | null
}

export interface LinhaDeHoje {
  congregacao: { id: string; nome: string; e_sede: boolean }
  /** Dinheiro vivo: espécie e moeda das ofertas, mais os dízimos em espécie. */
  especie: number
  pix: number
  cartao: number
  cultos_com_oferta: number
  lancamentos_de_dizimo: number
  tem_culto_na_agenda: boolean
}

export interface Hoje {
  data: string
  data_por_extenso: string
  /** O nome do dia da semana, na palavra do notebook: `Domingo`, `Segunda-feira`. */
  dia_da_semana: string
  /** Que dia é hoje para o SERVIDOR. A tela não olha o relógio do celular. */
  data_de_hoje: string
  dia_anterior: string
  /** Nulo quando não há para onde ir: a seta para frente fica apagada. */
  dia_seguinte: string | null
  competencia: string
  congregacoes: LinhaDeHoje[]
  total: { especie: number; pix: number; cartao: number }
  teve_movimento: boolean
}

export interface Inicio {
  competencia: string
  competencia_por_extenso: string
  congregacoes: LinhaDoInicio[]
  total: { entradas: number; saidas: number }
  /** Quanto entrou em dinheiro HOJE. Não depende do mês que a tela mostra. */
  hoje: Hoje
  pendencias: {
    aguardando_reabertura: { grupos: number; lancamentos: number }
    notebooks_sem_enviar: NotebookSemEnviar[]
    /** As saídas esperando decisão, da etapa 3 da parte 5. */
    saidas_esperando: { quantas: number; soma: number; sem_o_comprovante_exigido: number }
    /** As saídas que você aprovou, corrigidas por outra pessoa no notebook (0.3.24). */
    saidas_corrigidas_para_voce: {
      id: string
      congregacao: string | null
      data: string
      valor: number
      descricao: string
      corrigida_por: string
      em: string
    }[]
  }
}

// ------------------------------------------------------------------ a tela de Congregação

export interface LinhaDeOferta {
  culto_id: string
  data: string
  culto: string | null
  dia_semana: string
  cartao: number
  pix: number
  especie: number
  moeda: number
  total: number
  /** A oferta deste culto é de um departamento do Campo, e não do livro da congregação. */
  do_departamento: boolean
}

export interface LinhaDeDizimo {
  id: string
  data: string
  nome: string
  tipo: string
  forma: string
  valor: number
  e_estorno: boolean
}

/** O fechamento do mês de uma congregação. Fase 5 do espelho. */
export interface TotaisDoFechamento {
  ofertas: number
  ofertas_com_destino: number
  dizimos: number
  votos: number
  saidas: number
  saidas_cartao: number
  saidas_campo: number
  custo_real: number
  saldo: number
}

export interface VersaoDoFechamento {
  versao: number
  status: string
  assinado_por: string | null
  assinado_em: string | null
  motivo_da_reabertura: string | null
  saldo: number
  total_repasse: number
}

export interface TelaDoFechamento {
  congregacao: { id: string; nome: string; e_sede: boolean }
  competencia: string
  competencia_por_extenso: string
  situacao: 'ASSINADO' | 'REABERTO' | 'ABERTO' | 'TRAVADO'
  assinatura: VersaoDoFechamento | null
  versoes: VersaoDoFechamento[]
  totais: TotaisDoFechamento
  /** Os números são prévia, e não a fotografia assinada. Acende a tarja e a marca d'água. */
  e_previa: boolean
  ofertas_por_destino: { departamento: string; valor: number }[]
  repasses: {
    id: string; direcao: string; status: string; valor: number; competencia: string
    valor_calculado: number; valor_pago: number | null; motivo_valor_pago: string | null
    diferenca_sem_motivo: boolean
    /** O último estorno do pagamento (caso R6): quando, por quem e por quê. */
    estorno_do_pagamento?: { em: string; por: string | null; motivo: string | null } | null
  }[]
  total_repasse_no_razao: number
  impede_fechar: number
  /** As aguardando aprovação que seguram a assinatura (0.3.23); as pela conta do Campo à parte. */
  esperando_aprovacao: { total: number; pela_conta_do_campo: number }
}

/** Uma linha da folha de SAÍDAS do mês. Fase 4 do espelho. */
export interface LinhaDeSaida {
  id: string
  data: string
  categoria: string
  descricao: string
  valor: number
  status: string
  /** O nome de tela da situação, já traduzido pelo servidor. */
  situacao: string
  aprovado_por: string | null
  /** "paga por Fulano, pela pré-autorização de qualquer pessoa", ou nulo (0.3.21). */
  paga_por_outro: string | null
  bolso: string
  /** O nome curto do bolso, para a etiqueta. Nulo no caixa, que é o caso comum. */
  bolso_curto: string | null
  fundo: string | null
  custo_geral_do_campo: boolean
  tem_comprovante: boolean
  comprovante_exigido: boolean
  falta_comprovante: boolean
  do_extrato: boolean
  e_estorno: boolean
}

export interface FolhaDeSaidas {
  congregacao: { id: string; nome: string; e_sede: boolean }
  competencia: string
  competencia_por_extenso: string
  situacao_do_mes: SituacaoDoMes
  linhas: LinhaDeSaida[]
  total_aprovado: number
  total_pendente: number
  por_bolso: { CAIXA: number; CARTAO: number; CONTA_CAMPO: number }
  pendencias_para_fechar: number
}

export interface FolhasDaCongregacao {
  congregacao: { id: string; nome: string; e_sede: boolean }
  competencia: string
  competencia_por_extenso: string
  /** Que dia é hoje, pelo servidor. A folha marca esta linha. */
  hoje: string
  situacao: SituacaoDoMes
  fechamento: {
    versao: number | null
    assinado_por: string | null
    assinado_em: string | null
    motivo_da_reabertura: string | null
  }
  ofertas: {
    linhas: LinhaDeOferta[]
    totais: { cartao: number; pix: number; especie: number; moeda: number; total: number }
    de_departamento: number
  }
  dizimos: {
    linhas: LinhaDeDizimo[]
    total_dizimos: number
    total_votos: number
    total: number
  }
}

// ------------------------------------------------------------------ a tela do Campo

export type SituacaoDoCampo = 'ASSINADO' | 'REABERTO' | 'NAO_ASSINADO'

export interface CabecalhoDoCampo {
  linhas: string[]
  campo: string
  pastor: string
  tituloMovimento: string
  tituloFluxo: string
  cargoDoPastor: string
  segundaAssinatura: string
  linhaSaldoAnterior: string
  linhaSaldoDoMes: string
}

export interface ColunaDoCampo {
  id: string
  titulo: string
  rotulo: string | null
  conta: string | null
}

export interface LinhaDoMovimento {
  congregacao_id: string
  nome: string
  fechou: boolean
  travada: boolean
  /** Os números são a PRÉVIA de agora, de quem ainda não assinou o mês. */
  e_previa: boolean
  versao: number | null
  por_departamento: number[]
  entrada: number
  saida: number
  saldo: number
}

export interface LinhaDoFluxo {
  chave: string
  tipo: 'SALDO_ANTERIOR' | 'SALDO_DO_MES' | 'LINHA'
  descricao: string
  data: string | null
  entrada: number
  saida: number
  saldo: number
  nova: boolean
}

export interface VersaoDoCampo {
  versao: number
  status: string
  assinado_por: string | null
  assinado_em: string | null
  saldo_final: number
  motivo_da_reabertura: string | null
  tem_fotografia: boolean
}

export interface SaidaDoCampoPorCategoria {
  categoria: string
  valor: number
  quantas: number
}

/** A conta da PRÉVIA do mês aberto. O que ela é, e o que não é, está em `previa-do-campo.ts`. */
export interface ContaDaPrevia {
  saldo_do_mes_anterior: number
  mes_anterior_assinado: boolean
  entradas_das_congregacoes: number
  saidas_das_congregacoes: number
  envio_aos_departamentos: number
  repasse_a_matriz: number
  recargas_de_cartao: number
  saidas_pela_conta_do_campo: SaidaDoCampoPorCategoria[]
  total_das_saidas_pela_conta: number
  oferta_nao_identificada: number
  total_entradas: number
  total_saidas: number
  resultado: number
  linhas_do_fluxo_ja_ajustadas: number
}

export interface OQueAindaFalta {
  sem_sincronizar_hoje: {
    id: string
    nome: string
    ultimo_envio: string | null
    enviou_hoje: boolean
  }[]
  nao_fecharam_o_mes: { id: string; nome: string; travada: boolean }[]
  saidas_esperando: { quantas: number; soma: number }
}

export interface Previa {
  conta: ContaDaPrevia
  falta: OQueAindaFalta
}

export interface TelaDoCampo {
  campo: { nome: string }
  competencia: string
  competencia_por_extenso: string
  situacao: SituacaoDoCampo
  assinatura: {
    versao: number
    status: string
    assinado_por: string | null
    assinado_em: string | null
    motivo_da_reabertura: string | null
    e_a_atual: boolean
  } | null
  versoes: VersaoDoCampo[]
  cabecalho: CabecalhoDoCampo
  colunas: ColunaDoCampo[]
  movimento: LinhaDoMovimento[]
  total_do_movimento: { por_departamento: number[]; entrada: number; saida: number; saldo: number }
  /** Nulo no mês que o Campo ainda não assinou: o Fluxo só existe depois da assinatura. */
  fluxo: LinhaDoFluxo[] | null
  saldo_anterior: number | null
  total_entradas: number | null
  total_saidas: number | null
  saldo_final: number | null
  nao_fecharam: number
  tem_documento: boolean
  /** A prévia, só no mês ainda não assinado. */
  previa: Previa | null
  /** O cadastro do Campo chegou em dobro de dois notebooks. Nulo quando está limpo. */
  aviso_do_campo_em_dobro: string | null
}

// ------------------------------------------------------------------ Aguardando reabertura

export interface LancamentoEsperando {
  evento_id: string
  tabela: string
  registro_id: string
  acao: string
  usuario_nome: string
  data_do_lancamento: string | null
  criado_no_notebook_em: string
  recebido_em: string
  /** A frase curta que o pastor lê. Nunca leva nome de dizimista. */
  resumo: string
}

export interface GrupoEsperando {
  congregacao: { id: string | null; nome: string | null }
  competencia: string
  competencia_por_extenso: string
  /** CONGREGACAO | CAMPO: qual dos dois fechamentos está segurando. */
  tipo_de_fechamento: string
  quantidade: number
  primeiro_chegou_em: string
  ultimo_chegou_em: string
  fechamento: {
    versao: number
    assinado_por_nome: string | null
    assinado_em: string | null
  } | null
  lancamentos: LancamentoEsperando[]
}

export interface AEspera {
  grupos: GrupoEsperando[]
  total_de_lancamentos: number
}

export interface ReaberturaFeita {
  tipo: string
  competencia: string
  competencia_por_extenso: string
  congregacao: { id: string; nome: string } | null
  versao_reaberta: number
  lancamentos_aplicados: number
  repasses_cancelados: number
  repasses_mantidos: number
  congregacoes_destravadas: number
  recado: string
}

// ------------------------------------------------------------------ as saídas esperando

/**
 * Uma saída parada, esperando decisão. **Esta tela não aprova nada**: a etapa 3 da parte 5 é
 * só de leitura, e aprovar é a etapa 4.
 *
 * Nem o nome de quem recebeu o dinheiro nem a descrição da saída vêm do servidor. O porquê das
 * duas ausências está em `src/painel/saidas-pendentes.ts`.
 */
export interface SaidaEsperando {
  id: string
  congregacao: { id: string; nome: string | null }
  data: string
  valor: number
  categoria: string
  /** O que a tesouraria escreveu. Vem inteira; quem corta com reticências é a tela. */
  descricao: string
  lancado_por: string | null
  dias_esperando: number
  tem_comprovante: boolean
  comprovante_exigido: boolean
  /** Exige e não tem: é este que acende o aviso. */
  falta_comprovante: boolean
  /** Quem responde é o servidor, e não a tela. A tela só evita oferecer uma porta fechada. */
  voce_pode_aprovar: boolean
  por_que_nao: string | null
  /** A frase pronta da recusa. A tela não monta nenhuma. */
  recado_de_quem_nao_pode: string | null
  /** Estava aprovada, foi corrigida no notebook e voltou a aguardar (0.3.24). */
  corrigida: { por: string; em: string; aprovada_antes_por: string | null } | null
}

export interface SaidasEsperando {
  saidas: SaidaEsperando[]
  total: number
  soma: number
  sem_o_comprovante_exigido: number
  voce_pode_aprovar: number
  /** A senha desta sessão ainda vale, e por quantos minutos. Quem decide é o servidor. */
  a_senha_ja_vale: boolean
  minutos_que_a_senha_vale: number
  minutos_da_senha: number
}

/** O que volta quando o painel aprova uma saída. Etapa 4 da parte 5. */
export interface SaidaAprovada {
  id: string
  congregacao: { id: string; nome: string | null }
  valor: number
  categoria: string
  status: string
  aprovado_em: string
  sequencia: number
  recado: string
  /**
   * Como ficou a janela de vinte minutos depois desta aprovação.
   *
   * A tela tira a saída da lista na hora, sem esperar o pedido seguinte, e por isso precisa dos
   * números novos: sem eles ela pediria a senha de novo na aprovação seguinte.
   */
  a_senha_ja_vale: boolean
  minutos_que_a_senha_vale: number
  minutos_da_senha: number
}

// ------------------------------------------------------------------ a administração

export interface NotebookNaTela {
  id: string
  nome: string
  congregacao: { id: string; nome: string | null }
  ativo: boolean
  motivo_inativo: string | null
  de_ensaio: boolean
  ultimo_envio: string | null
  dias_sem_enviar: number | null
  inscrito_em: string
  preocupa: boolean
}

export interface TelaDeNotebooks {
  notebooks: NotebookNaTela[]
  ativos: number
  cortados: number
  de_ensaio: number
  preocupam: number
  posso_administrar: boolean
  /** Só o administrador máximo gera código de notebook novo. */
  posso_inscrever: boolean
  congregacoes: { id: string; nome: string; e_sede: boolean }[]
}

export interface NotebookMudado {
  dispositivo_id: string
  nome: string
  ativo: boolean
  recado: string
}

export interface CodigoDeInscricao {
  codigo: string
  congregacao: { id: string; nome: string }
  expira_em: string
  validade_em_horas: number
  recado: string
}

export type ComoNoNotebook = 'ATIVA' | 'NAO_CHEGOU' | 'DESATIVADA' | 'EXCLUIDA'

export interface PessoaDoPainelNaTela {
  id: string
  nome: string
  email: string
  ativo: boolean
  ultimo_acesso: string | null
  bloqueada_ate: string | null
  no_notebook: {
    usuario_id: string
    nome: string | null
    congregacao: string | null
    como: ComoNoNotebook
    explicacao: string
    e_admin_maximo: boolean
  } | null
  alcance: string
  tem_segundo_fator: boolean
}

export interface QuemPodeTerConta {
  usuario_id: string
  nome: string
  congregacao: string | null
  e_admin_maximo: boolean
  /** O e-mail que ela já tem no notebook, para a caixa vir preenchida. Nulo quando não tem. */
  email: string | null
}

export interface TelaDePessoas {
  pessoas: PessoaDoPainelNaTela[]
  aviso_do_segundo_fator: string
  /** Quem do notebook ainda pode ganhar conta. Vazio para quem não é administrador máximo. */
  quem_pode_ter_conta: QuemPodeTerConta[]
  /** Por onde o sistema manda e-mail. `arquivo` quer dizer que nada sai daqui. */
  envio_de_email: { servico: string }
}

/** Uma pessoa que pode trocar a senha com o endereço do link. */
export interface QuemOLinkAlcanca {
  /** A chave opaca que volta para o servidor na hora de trocar. */
  quem: string
  nome: string
  /** O que a senha dela abre, numa frase. */
  o_que_troca: string
  /** Falso no primeiro acesso: a tela diz "Criar a senha", e não "Trocar a senha". */
  ja_tem_senha?: boolean
}

export interface OLinkAberto {
  vale: true
  minutos: number
  quem: QuemOLinkAlcanca[]
}

export interface SenhaTrocadaPeloLink {
  trocada: true
  nome: string
  vale_no_notebook: boolean
  entra_no_painel: boolean
  tem_segundo_fator: boolean
}

/**
 * O que a tela de Sobre pergunta ao servidor. **A única chamada que responde sem banco**, e
 * uma das duas que não precisam de sessão.
 */
export interface Sobre {
  sistema: string
  versao: string
  /** `null` quando não deu para descobrir a data do programa que está rodando. */
  de_quando_e: string | null
  no_ar_desde: string
  emails_por: string
  /** `null` quando ninguém configurou quem cuida. A tela diz isso com essas palavras. */
  quem_cuida: string | null
  aviso_de_titularidade: string
  /** Qual ambiente é este. A faixa do topo sai daqui. Opcional para servidor de antes de 16/10. */
  ambiente?: 'producao' | 'teste' | 'auditoria' | 'desenvolvimento'
}

/**
 * Onde se baixa o programa do notebook. **A outra chamada que responde sem sessão.**
 *
 * `tem_instalador: false` quando quem hospeda não configurou endereço nenhum, e aí não existe
 * botão. A `versao` vem nula quando o servidor não conseguiu perguntar lá fora, e nesse caso o
 * botão aparece sem o número: o download não depende de a pergunta da versão ter dado certo.
 */
export type Instalador =
  | { tem_instalador: false }
  | {
      tem_instalador: true
      endereco: string
      versao: string | null
      publicada_em: string | null
    }

export interface LinhaDaAuditoria {
  id: string
  acao: string
  o_que_foi: string
  congregacao: string | null
  notebook: string | null
  quem: string | null
  tabela: string | null
  registro_id: string | null
  motivo: string | null
  detalhe: Record<string, unknown> | null
  quando: string
}

export interface TelaDeAuditoria {
  linhas: LinhaDaAuditoria[]
  tem_mais: boolean
}

/**
 * O que a entrada devolve. Dois fins possíveis, e a tela precisa saber qual é.
 *
 * `dentro: false` com `falta: 'segundo_fator'` **não é recusa**: a senha estava certa e falta o
 * código. O servidor já guardou, num cookie de cinco minutos, de quem é a vez.
 */
export type FimDaEntrada = ({ dentro: true } & Eu) | { dentro: false; falta: 'segundo_fator' }

export interface SegundoFatorComecado {
  /** O segredo em letras, para quem não conseguir ler o quadrado. */
  segredo: string
  /** A imagem do quadrado, pronta para um `<img src>`. */
  quadrado: string
  ja_estava_ligado: boolean
}

export interface ComoEstaOSegundoFator {
  ligado: boolean
  codigos_que_sobram: number | null
  /** Desligado, mas já teve um: quem hospeda desligou, e a pessoa cadastra o aplicativo de novo. */
  ja_teve?: boolean
}

/** A resposta de trocar a minha senha, de dentro do painel. */
export interface SenhaTrocadaNoPainel {
  trocada: true
  /** A senha também vale nos notebooks do Campo, depois que cada um sincronizar. */
  vale_no_notebook: boolean
  outras_sessoes_encerradas: number
  aviso_por_email: boolean
  aviso: string
}

export const servidor = {
  eu: () => api.get<QuemEsta>('/v1/painel/eu'),
  /** Sem sessão, e sem banco: ver `src/rotas/sobre.ts`, do lado do servidor. */
  sobre: () => api.get<Sobre>('/v1/sobre'),
  /** Sem sessão também: a tela de entrar pergunta antes de qualquer senha. */
  instalador: () => api.get<Instalador>('/v1/instalador'),
  entrar: (email: string, senha: string) =>
    api.post<FimDaEntrada>('/v1/painel/entrar', { email, senha }),
  entrarComOCodigo: (codigo: string) =>
    api.post<{ dentro: true } & Eu>('/v1/painel/entrar/codigo', { codigo }),
  segundoFator: () => api.get<ComoEstaOSegundoFator>('/v1/painel/segundo-fator'),
  comecarOSegundoFator: () =>
    api.post<SegundoFatorComecado>('/v1/painel/segundo-fator/comecar'),
  confirmarOSegundoFator: (codigo: string) =>
    api.post<{ ligado: true; codigos_de_emergencia: string[] }>(
      '/v1/painel/segundo-fator/confirmar', { codigo }
    ),
  desligarOMeuSegundoFator: (senha: string, codigo: string) =>
    api.post<{ ligado: false }>('/v1/painel/segundo-fator/desligar', { senha, codigo }),
  desligarOSegundoFatorDe: (id: string, motivo: string, senha: string, codigo: string) =>
    api.post<{ ligado: false; havia_segundo_fator: boolean }>(
      `/v1/painel/pessoas/${encodeURIComponent(id)}/desligar-segundo-fator`,
      { motivo, senha, codigo }
    ),
  /** A senha de agora, a nova, e o número do aplicativo quando a conta tem segundo fator. */
  trocarAMinhaSenha: (senhaAtual: string, senhaNova: string, codigo: string | undefined) =>
    api.post<SenhaTrocadaNoPainel>('/v1/painel/trocar-senha', {
      senha_atual: senhaAtual,
      senha_nova: senhaNova,
      ...(codigo === undefined ? {} : { codigo })
    }),
  sair: () => api.post<{ saiu: boolean }>('/v1/painel/sair'),

  /*
   * O TREINAMENTO: entrar, sair e recomecar.
   *
   * Nenhuma das tres manda o Campo no corpo. O gemeo e deduzido de quem e a conta, do lado do
   * servidor, e um gatilho no banco recusa sessao apontando para qualquer outro Campo. E assim
   * que a regra permanente do projeto continua literal: o `campo_id` nao vem do corpo do pedido.
   */
  irParaOTreinamento: () =>
    api.post<{ treinamento: boolean; campo: string }>('/v1/painel/treinamento/entrar'),
  sairDoTreinamento: () =>
    api.post<{ treinamento: boolean }>('/v1/painel/treinamento/sair'),
  recomecarOTreinamento: () =>
    api.post<{ recomecado: boolean; geracao: number; linhas: number; recado: string }>(
      '/v1/painel/treinamento/recomecar'
    ),
  /**
   * As três chamadas do "esqueci minha senha". **Nenhuma delas precisa de sessão**: quem está
   * aqui é justamente quem não consegue entrar.
   */
  esqueciASenha: (email: string) =>
    api.post<{ pedido: true; recado: string }>('/v1/painel/senha/esqueci', { email }),
  abrirOLink: (token: string) =>
    api.get<OLinkAberto>(`/v1/painel/senha/link/${encodeURIComponent(token)}`),
  trocarPeloLink: (token: string, quem: string, senhaNova: string) =>
    api.post<SenhaTrocadaPeloLink>('/v1/painel/senha/trocar', {
      token,
      quem,
      senha_nova: senhaNova
    }),
  mandarEmailDeTeste: () =>
    api.post<{ na_fila: true; id: string; servico: string }>('/v1/painel/email-de-teste'),
  inicio: (competencia?: string, dia?: string | null) => {
    const busca = new URLSearchParams()
    if (competencia) busca.set('competencia', competencia)
    if (dia) busca.set('dia', dia)
    const perguntas = busca.toString()
    return api.get<Inicio>(`/v1/painel/inicio${perguntas ? `?${perguntas}` : ''}`)
  },
  congregacao: (id: string, competencia?: string | null) =>
    api.get<FolhasDaCongregacao>(
      `/v1/painel/congregacoes/${encodeURIComponent(id)}` +
      (competencia ? `?competencia=${competencia}` : '')
    ),
  /** A folha de SAÍDAS daquele mês, só leitura. Tela própria: ver a rota, do lado do servidor. */
  saidasDaCongregacao: (id: string, competencia?: string | null) =>
    api.get<FolhaDeSaidas>(
      `/v1/painel/congregacoes/${encodeURIComponent(id)}/saidas` +
      (competencia ? `?competencia=${competencia}` : '')
    ),
  /** O fechamento daquele mês: a fotografia assinada, ou a prévia do mês aberto. */
  fechamentoDaCongregacao: (id: string, competencia?: string | null) =>
    api.get<TelaDoFechamento>(
      `/v1/painel/congregacoes/${encodeURIComponent(id)}/fechamento` +
      (competencia ? `?competencia=${competencia}` : '')
    ),
  /* A fase 6 do espelho: dizimistas, repasses, conferência da maquininha e agenda. */
  dizimistas: (id: string, competencia?: string | null) =>
    api.get<RelacaoDeDizimistas>(
      `/v1/painel/congregacoes/${encodeURIComponent(id)}/dizimistas` +
      (competencia ? `?competencia=${competencia}` : '')
    ),
  repassesDaCongregacao: (id: string, competencia?: string | null) =>
    api.get<RepassesDaCongregacao>(
      `/v1/painel/congregacoes/${encodeURIComponent(id)}/repasses` +
      (competencia ? `?competencia=${competencia}` : '')
    ),
  repassesDoCampo: (competencia?: string | null) =>
    api.get<RepassesDoCampo>('/v1/painel/campo/repasses' + (competencia ? `?competencia=${competencia}` : '')),
  conferenciaDoCartao: (competencia?: string | null) =>
    api.get<ConferenciaDoCartao>(
      '/v1/painel/campo/conferencia-do-cartao' + (competencia ? `?competencia=${competencia}` : '')
    ),
  agenda: (id: string, competencia?: string | null) =>
    api.get<AgendaDoMes>(
      `/v1/painel/congregacoes/${encodeURIComponent(id)}/agenda` +
      (competencia ? `?competencia=${competencia}` : '')
    ),
  aguardando: () => api.get<AEspera>('/v1/painel/aguardando'),
  /** Só leitura. Não existe caminho de aprovar nesta parte: ver a etapa 3 da parte 5. */
  saidasPendentes: () => api.get<SaidasEsperando>('/v1/painel/saidas-pendentes'),
  /**
   * Aprovar uma saída. **Pede a senha, e só ela.**
   *
   * A senha vai só na primeira: depois dela, a janela de vinte minutos deixa as outras serem um
   * toque. Se a janela fechar no meio, o servidor responde `senha_necessaria` e a tela pede de
   * novo; quem decide é ele, nunca esta página.
   *
   * Não existe `recusarSaida`, e isso é decisão, não esquecimento: o painel só aprova nesta
   * parte, e recusar continua sendo no notebook.
   */
  aprovarSaida: (id: string, senha?: string) =>
    api.post<SaidaAprovada>(
      `/v1/painel/saidas/${encodeURIComponent(id)}/aprovar`,
      senha === undefined ? {} : { senha }
    ),
  notebooks: () => api.get<TelaDeNotebooks>('/v1/painel/notebooks'),
  cortarNotebook: (id: string, motivo: string, senha: string, codigo: string) =>
    api.post<NotebookMudado>(
      `/v1/painel/notebooks/${encodeURIComponent(id)}/cortar`, { motivo, senha, codigo }
    ),
  devolverAcesso: (id: string, motivo: string, senha: string, codigo: string) =>
    api.post<NotebookMudado>(
      `/v1/painel/notebooks/${encodeURIComponent(id)}/devolver-acesso`, { motivo, senha, codigo }
    ),
  codigoDeInscricao: (congregacaoId: string, senha: string, codigo: string) =>
    api.post<CodigoDeInscricao>('/v1/painel/notebooks/codigo', {
      congregacao_id: congregacaoId,
      senha,
      codigo
    }),
  pessoas: () => api.get<TelaDePessoas>('/v1/painel/pessoas'),
  criarConta: (conta: {
    email: string
    nome: string
    usuario_id: string
    senha: string
    codigo: string
  }) =>
    api.post<{ id: string; email: string; nome: string; entra_como: string }>(
      '/v1/painel/pessoas', conta
    ),
  auditoria: (filtro: { acao?: string; antes_de?: string } = {}) => {
    const partes = [
      ...(filtro.acao ? [`acao=${encodeURIComponent(filtro.acao)}`] : []),
      ...(filtro.antes_de ? [`antes_de=${encodeURIComponent(filtro.antes_de)}`] : [])
    ]
    return api.get<TelaDeAuditoria>(
      `/v1/painel/auditoria${partes.length === 0 ? '' : `?${partes.join('&')}`}`
    )
  },
  reabrir: (pedido: {
    tipo: 'CONGREGACAO' | 'CAMPO'
    congregacao_id?: string | null
    competencia: string
    motivo: string
    senha: string
  }) => api.post<ReaberturaFeita>('/v1/painel/aguardando/reabrir', {
    tipo: pedido.tipo,
    ...(pedido.congregacao_id ? { congregacao_id: pedido.congregacao_id } : {}),
    competencia: pedido.competencia,
    motivo: pedido.motivo,
    senha: pedido.senha
  }),
  campo: (competencia?: string | null, versao?: number | null) => {
    const partes = [
      ...(competencia ? [`competencia=${competencia}`] : []),
      ...(versao ? [`versao=${versao}`] : [])
    ]
    return api.get<TelaDoCampo>(`/v1/painel/campo${partes.length === 0 ? '' : `?${partes.join('&')}`}`)
  }
}

// ------------------------------------------------------------------ a fase 6 do espelho

/** Ver `src/painel/dizimistas.ts`, do lado do servidor. */
export interface RelacaoDeDizimistas {
  congregacao: { id: string; nome: string }
  competencia: string
  competencia_por_extenso: string
  pessoas: {
    membro_id: string
    nome: string
    dizimos: number
    votos: number
    total: number
    lancamentos: number
  }[]
  total_dizimos: number
  total_votos: number
  total: number
  lancamentos: number
  pode_baixar_o_pdf: boolean
}

export interface RegraEmVigor {
  resumo: string
  geral: boolean
  vigencia_inicio: string | null
  vigencia_fim: string | null
}

export interface LinhaDeRepasseNaTela {
  id: string
  congregacao_id: string
  origem: string
  destino: string
  direcao: string
  avulso: boolean
  departamento_destino: string | null
  motivo: string
  valor_calculado: number
  valor_pago: number | null
  motivo_valor_pago: string | null
  status: string
  situacao: string
  aprovado_por: string | null
  estorno_do_pagamento: { em: string; por: string | null; motivo: string | null } | null
}

/** Ver `src/painel/repasses-do-mes.ts`, do lado do servidor. */
export interface RepassesDaCongregacao {
  congregacao: { id: string; nome: string }
  competencia: string
  competencia_por_extenso: string
  regra_em_vigor: RegraEmVigor
  linhas: LinhaDeRepasseNaTela[]
  historico: {
    competencia: string
    competencia_por_extenso: string
    calculado: number
    pago: number
    aguardando: number
    a_pagar: number
  }[]
}

export interface PorSituacao {
  aguardando: number
  aprovado: number
  pago: number
}

export interface RepassesDoCampo {
  competencia: string
  competencia_por_extenso: string
  linhas: LinhaDeRepasseNaTela[]
  regras: { congregacao_id: string; congregacao: string; regra: RegraEmVigor }[]
  resumo: {
    recebido_das_congregacoes: PorSituacao
    enviado_as_congregacoes: PorSituacao
    enviado_a_matriz: PorSituacao
  }
}

/** Ver `src/painel/conferencia-do-cartao.ts`, do lado do servidor. */
export interface ConferenciaDoCartao {
  competencia: string
  competencia_por_extenso: string
  linhas: {
    congregacao_id: string
    congregacao: string
    cartao_nas_folhas: number
    maquina_no_banco: number
    diferenca_cartao: number
    pix_nas_folhas: number
    pix_no_banco: number
    diferenca_pix: number
    deposito_no_banco: number
  }[]
  avisos: string[]
  maquinas: { congregacao_id: string; congregacao: string; numero: string; descricao: string | null }[]
}

/** Ver `src/painel/agenda.ts`, do lado do servidor. */
export interface AgendaDoMes {
  congregacao: { id: string; nome: string }
  competencia: string
  competencia_por_extenso: string
  padrao_da_semana: {
    resumo: string
    inicial: boolean
    dias: { dia_semana: string; cultos: string[] }[]
    por_semana: number
  }
  cultos: {
    id: string
    data: string
    dia: string
    dia_semana: string
    culto: string | null
    destino_da_oferta: string | null
    oferta_lancada: boolean
  }[]
  com_oferta: number
}
